/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Warning } from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { mapProductStatusToWarningStatus } from '../../utils/sig-airmet-util';

export const EDGE_PADDING = 24;

export const getEffectiveStatus = (warning: Warning): PublicWarningStatus =>
  warning.status ?? mapProductStatusToWarningStatus(warning.productStatus);

const STATUS_SORT_ORDER: Record<PublicWarningStatus, number> = {
  [PublicWarningStatus.PUBLISHED]: 0,
  [PublicWarningStatus.DRAFT_PUBLISHED]: 0,
  [PublicWarningStatus.EXPIRED]: 1,
  [PublicWarningStatus.WITHDRAWN]: 1,
  [PublicWarningStatus.IGNORED]: 1,
  [PublicWarningStatus.DRAFT]: 2,
  [PublicWarningStatus.TODO]: 3,
};

export const compareWarningsByStatus = (
  warningA: Warning,
  warningB: Warning,
): number =>
  STATUS_SORT_ORDER[getEffectiveStatus(warningA)] -
  STATUS_SORT_ORDER[getEffectiveStatus(warningB)];

export type TickType = 'day' | 'major' | 'dashed';

export interface Tick {
  time: Date;
  type: TickType;
}

export type BlockSize = 'xxs' | 'xs' | 's' | 'l';

const SIZE_THRESHOLDS = { xxs: 24, xs: 50, sm: 100 };

export const getTimelineX = (
  targetTime: Date,
  timelineStart: Date,
  timelineEnd: Date,
  drawableWidth: number,
): number => {
  const totalTimelineDuration = timelineEnd.getTime() - timelineStart.getTime();
  if (totalTimelineDuration <= 0) {
    return 0;
  }
  const elapsedMs = targetTime.getTime() - timelineStart.getTime();
  return EDGE_PADDING + (elapsedMs / totalTimelineDuration) * drawableWidth;
};

export const getTimeFromX = (
  x: number,
  timelineStart: Date,
  timelineEnd: Date,
  drawableWidth: number,
): Date => {
  const totalTimelineDuration = timelineEnd.getTime() - timelineStart.getTime();
  if (drawableWidth <= 0) {
    return new Date(timelineStart);
  }
  const adjustedX = x - EDGE_PADDING;
  const ratio = adjustedX / drawableWidth;
  const timeMs = timelineStart.getTime() + ratio * totalTimelineDuration;
  return new Date(timeMs);
};

export const generateTicks = (
  timelineStart: Date,
  timelineEnd: Date,
): Tick[] => {
  const ticks: Tick[] = [];
  const hourInterval = 3;

  const current = new Date(timelineStart);
  current.setUTCMinutes(0, 0, 0);
  if (current < timelineStart) {
    current.setUTCHours(current.getUTCHours() + 1);
  }

  while (current <= timelineEnd) {
    const hour = current.getUTCHours();

    if (hour === 0) {
      ticks.push({ time: new Date(current), type: 'day' });
    } else if (hour % hourInterval === 0) {
      ticks.push({ time: new Date(current), type: 'major' });
    } else {
      ticks.push({ time: new Date(current), type: 'dashed' });
    }

    current.setUTCHours(current.getUTCHours() + 1);
  }

  return ticks;
};

export const getTickLineStyle = (
  tickType: TickType,
): { width: string; borderLeft: string } => {
  if (tickType === 'day') {
    return { width: '2px', borderLeft: '2px solid #ABC3FF' };
  }
  if (tickType === 'dashed') {
    return { width: '1px', borderLeft: '1px dashed #E7ECFA' };
  }
  return { width: '1px', borderLeft: '1px solid #90caf9' };
};

export const getBlockSize = (width: number): BlockSize => {
  if (width < SIZE_THRESHOLDS.xxs) {
    return 'xxs';
  }
  if (width < SIZE_THRESHOLDS.xs) {
    return 'xs';
  }
  if (width < SIZE_THRESHOLDS.sm) {
    return 's';
  }
  return 'l';
};
